import { DUTIES, EDUCATION_RANK } from './posting-duties';
import type { PostingReading, Requirement } from './posting-requirements';
import { QUESTION_GROUPS } from './resume-groups';
import { OccupationTool, toolForms, toolsNamed } from './specific-skills';

export { EDUCATION_RANK };

/**
 * T.10 S15 (D92). What a posting asks for, held against the member's own
 * evidence only: their lines, the skills they added or confirmed, their
 * certifications, their jobs' years and their education. A tool that is
 * only common in their occupation is offered, never counted as theirs.
 */

export interface EvidenceLine {
  id: string;
  text: string;
}

export interface EvidenceJob {
  key: string;
  title: string;
  civilianTitle?: string;
  months?: number | null;
  lines: EvidenceLine[];
}

export interface MemberEvidence {
  /** Kept jobs only (S13: a removed job is not kept, so never sent). */
  jobs: EvidenceJob[];
  /** Skills the member added, typed, imported from a resume or tapped. */
  confirmed: string[];
  certifications: string[];
  /** EDUCATION_RANK of the member's highest finished level; null when none. */
  educationRank: number | null;
  occupationTools: OccupationTool[];
}

export interface CheckShows {
  requirement: Requirement;
  where: string;
  lineId?: string;
  jobKey?: string;
}

export interface CheckMayHave {
  requirement: Requirement;
  why: string;
}

export interface CheckMissing {
  requirement: Requirement;
  action: 'win' | 'certification' | 'none';
  /** A tool the member may tap as true, as well as add a win about. */
  canTap?: boolean;
  question?: string;
  jobKey?: string;
  note?: string;
}

export interface Checklist {
  shows: CheckShows[];
  mayHave: CheckMayHave[];
  missing: CheckMissing[];
  interview: string[];
  total: number;
}

export function checklistLine(c: Pick<Checklist, 'shows' | 'total'>): string {
  return c.total
    ? `Your resume shows ${c.shows.length} of the ${c.total} things this posting asks for.`
    : 'This posting does not name anything CPD can match yet.';
}

const COMPUTER_GROUP = 6;
const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
/** A name inside a text as a whole name: not after a letter or digit, not before one or a "+" (the rewrite guardrail's rule). */
export const namesWhole = (text: string, name: string): boolean =>
  !!name.trim() && new RegExp(`(^|[^A-Za-z0-9])${escapeRe(name.trim())}(?![A-Za-z0-9+])`, 'i').test(text);
const lower = (s: string) => s.toLowerCase().trim();
const quoted = (t: string) => `"${t.length > 90 ? `${t.slice(0, 87).trim()}...` : t}"`;
const questionOf = (group: number) => QUESTION_GROUPS.find((g) => g.id === group)?.question;
const yearsText = (months: number) => {
  const y = Math.round(months / 12);
  return y >= 1 ? `about ${y} year${y === 1 ? '' : 's'}` : 'less than a year';
};
const STOP = new Set(['and', 'or', 'the', 'with', 'for', 'experience', 'years', 'year', 'work', 'working', 'related', 'similar', 'field', 'role', 'roles']);

function aboutWords(about: string): string[] {
  return (about.toLowerCase().match(/[a-z][a-z+#-]{2,}/g) ?? []).filter((w) => !STOP.has(w));
}

function lineWith(jobs: readonly EvidenceJob[], test: (t: string) => boolean): { job: EvidenceJob; line: EvidenceLine } | null {
  for (const job of jobs) {
    const line = job.lines.find((l) => test(l.text));
    if (line) {
      return { job, line };
    }
  }
  return null;
}

/** The line that leads with a close word (its earliest place in a line), so the closest wording is quoted. */
function closestLine(jobs: readonly EvidenceJob[], re: RegExp): { job: EvidenceJob; line: EvidenceLine } | null {
  let best: { job: EvidenceJob; line: EvidenceLine; at: number } | null = null;
  for (const job of jobs) {
    for (const line of job.lines) {
      const m = re.exec(line.text);
      if (m && (!best || m.index < best.at)) {
        best = { job, line, at: m.index };
      }
    }
  }
  return best;
}

export function buildChecklist(reading: PostingReading, ev: MemberEvidence): Checklist {
  const shows: CheckShows[] = [];
  const mayHave: CheckMayHave[] = [];
  const missing: CheckMissing[] = [];
  const firstJob = ev.jobs[0]?.key;
  const confirmed = new Set(ev.confirmed.map(lower));
  for (const r of reading.requirements) {
    if (r.kind === 'tool') {
      const forms = r.forms?.length ? r.forms : [r.label];
      const asTools = forms.map((f) => ({ name: f, hot: false }));
      if (forms.some((f) => confirmed.has(lower(f))) || ev.confirmed.some((c) => toolForms(c).some((f) => forms.some((x) => lower(x) === lower(f))))) {
        shows.push({ requirement: r, where: 'Your skills (you added it)' });
        continue;
      }
      const hit = lineWith(ev.jobs, (t) => toolsNamed(t, asTools).length > 0);
      if (hit) {
        shows.push({ requirement: r, where: quoted(hit.line.text), lineId: hit.line.id, jobKey: hit.job.key });
        continue;
      }
      const common = ev.occupationTools.find((t) => toolForms(t.name).some((f) => forms.some((x) => lower(x) === lower(f))));
      if (common) {
        mayHave.push({ requirement: r, why: `O*NET lists it as technology often used by ${common.occupation || 'people in your occupation'}. CPD does not know you used it.` });
      } else {
        missing.push({ requirement: r, action: 'win', canTap: true, question: questionOf(COMPUTER_GROUP), ...(firstJob ? { jobKey: firstJob } : {}) });
      }
    } else if (r.kind === 'certification') {
      const forms = (r.forms?.length ? r.forms : [r.label]).map(lower);
      // Review H2: a whole name, never a piece of one ("A+" is not in "CySA+" or "Data+").
      if (ev.certifications.some((c) => forms.some((f) => namesWhole(c, f)))) {
        shows.push({ requirement: r, where: 'Your certifications' });
      } else {
        missing.push({ requirement: r, action: 'certification', ...(r.note ? { note: r.note } : {}) });
      }
    } else if (r.kind === 'years') {
      const need = (r.years ?? 0) * 12;
      const words = aboutWords(r.about ?? '');
      const relevant = ev.jobs.filter((j) => {
        const text = lower([j.title, j.civilianTitle ?? '', ...j.lines.map((l) => l.text)].join(' '));
        return !words.length || words.some((w) => new RegExp(`\\b${w.replace(/[+#]/g, '\\$&')}`).test(text));
      });
      const best = relevant.find((j) => (j.months ?? 0) >= need);
      const total = ev.jobs.reduce((n, j) => n + (j.months ?? 0), 0);
      if (best) {
        shows.push({ requirement: r, where: `${best.title}, ${yearsText(best.months ?? 0)}`, jobKey: best.key });
      } else if (total >= need && total > 0) {
        mayHave.push({ requirement: r, why: `Your jobs add up to ${yearsText(total)}. Tap if that work counts for this.` });
      } else {
        missing.push({ requirement: r, action: 'none', note: total ? `Your resume shows ${yearsText(total)}.` : 'Your resume does not show this yet.' });
      }
    } else if (r.kind === 'education') {
      if (ev.educationRank !== null && ev.educationRank >= (r.level ?? 0)) {
        shows.push({ requirement: r, where: 'Your education' });
      } else {
        missing.push({ requirement: r, action: 'none', note: r.note ? `Not on your profile yet (the posting takes ${r.note}).` : 'Not on your profile yet.' });
      }
    } else {
      const duty = DUTIES.find((d) => d.id === r.dutyId);
      if (!duty) {
        continue;
      }
      const strong = lineWith(ev.jobs, (t) => duty.strong.test(t));
      const close = strong ? null : closestLine(ev.jobs, duty.close);
      if (strong) {
        shows.push({ requirement: r, where: quoted(strong.line.text), lineId: strong.line.id, jobKey: strong.job.key });
      } else if (close) {
        mayHave.push({ requirement: r, why: `Close to what you wrote: ${quoted(close.line.text)}` });
      } else {
        missing.push({ requirement: r, action: 'win', question: questionOf(duty.group), ...(firstJob ? { jobKey: firstJob } : {}) });
      }
    }
  }
  return { shows, mayHave, missing, interview: [...reading.interview], total: shows.length + mayHave.length + missing.length };
}

/** Levels that are coursework already taken ("Some college courses"): they count as held even while the member is still studying. */
const COURSEWORK = new Set([4, 13, 15]);

/**
 * The member's highest level as a rank, from api.educationlevel ids. A
 * level still in progress counts as the rank below it (a bachelor's under
 * way still shows college coursework); coursework levels count as held.
 */
export function educationRankOf(levels: readonly { levelId?: number | null; completed?: boolean | null }[]): number | null {
  const ranks = levels
    .filter((l) => l.levelId && EDUCATION_RANK[l.levelId] !== undefined)
    .map((l) => {
      const r = EDUCATION_RANK[l.levelId as number];
      return l.completed === false && !COURSEWORK.has(l.levelId as number) ? Math.max(0, r - 1) : r;
    });
  return ranks.length ? Math.max(...ranks) : null;
}
